import { Fragment, useEffect, useRef, useState } from 'react'
import { Link } from 'react-router-dom'
import { useQuery } from '@tanstack/react-query'
import { supabase } from '@/lib/supabase'
import { useEffectiveClientId } from '@/hooks/useAuth'
import { useWatchState } from '@/hooks/useAlerts'
import { useRecordsState } from '@/hooks/useRecords'
import { ROUTES } from './chrome/nav'
import { useSeedBalance, useSeedHistory, PRICE_LIST, LEDGER_LABELS, SEED_COSTS } from '@/hooks/useSeeds'
import { usePlan } from '@/hooks/useConfig'
import { useSeedFx } from '@/lib/seedFx'
import type { SeedBalance, SeedLedgerEntry } from '@/types/database'

export function SeedGlyph({ className }: { className?: string }) {
  return (
    <svg className={className} viewBox="-4 -15 8 30" aria-hidden="true">
      <path d="M0.4,-14.5 C2.6,-9 3.1,-2 2.6,4 C2.2,9 1.2,12.5 -0.2,14.5 C-1.6,12 -2.6,8 -2.8,3 C-3,-3 -2,-9.5 0.4,-14.5Z" fill="currentColor" />
    </svg>
  )
}

/**
 * The cost on a button (Brian, Oct 22: "Costs are now on the buttons: Build ·
 * 12, Pull 5 more · 3 seeds"). Free actions show nothing.
 */
export function SeedCost({ n, unit = false }: { n: number; unit?: boolean }) {
  if (n <= 0) return null
  return <span className="seed-badge">{' · '}{n}{unit ? ` ${n === 1 ? 'seed' : 'seeds'}` : ''}</span>
}

/** Everything the chip, the panel and the desktop meter need, from one balance. */
export interface SeedView {
  b: SeedBalance | undefined | null
  total: number
  weekly: number
  /** This week's seeds: the plan's week plus what rolled over. */
  week: number
  rollover: number
  borrowed: number
  purchased: number
  granted: number
  /** Share of the week used, 0 to 100. */
  pct: number
  weekStories: number
  low: boolean
  backWhen: string
  /** Days into the week (at least half a day). */
  into: number
  /** The day this week's seeds run out at this pace ('today', 'tomorrow',
   *  'Thursday'), or null when they last to Sunday (app-data.js meter()). */
  runsOut: string | null
}

const DAYS = ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday']

export function useSeedView(): SeedView {
  const { data: b } = useSeedBalance()
  const held = useSeedFx((s) => s.held)
  const release = useSeedFx((s) => s.release)
  // A fresh balance from the server already reflects the spend.
  useEffect(() => { release() }, [b, release])

  let h = held
  const weekly = Math.max(0, (b?.weekly_remaining ?? 0) - h); h -= (b?.weekly_remaining ?? 0) - weekly
  const granted = Math.max(0, (b?.granted_remaining ?? 0) - h); h -= (b?.granted_remaining ?? 0) - granted
  const purchased = Math.max(0, (b?.purchased_remaining ?? 0) - h)
  const week = b?.seeds_week ?? 0
  const used = Math.max(0, week - weekly)
  const pct = week > 0 ? Math.min(100, Math.round((100 * used) / week)) : 0
  // Brian's meter(): back tomorrow on Sunday, otherwise Monday.
  let backWhen = 'Monday'
  let into = 0.5
  let runsOut: string | null = null
  if (b?.week_start) {
    const start = new Date(b.week_start + 'T00:00:00').getTime()
    into = Math.max(0.5, (Date.now() - start) / 864e5)
    if (into >= 6) backWhen = 'tomorrow'
    const rate = used / into
    const outAt = rate > 0 ? into + weekly / rate : Infinity
    const today = Math.floor(into)
    if (outAt < 7 && weekly > 0) {
      const d = Math.floor(outAt)
      runsOut = d <= today ? 'today' : d === today + 1 ? 'tomorrow' : DAYS[d]
    }
  }
  return {
    b, weekly, week, purchased, granted, total: weekly + granted + purchased, pct,
    rollover: b?.rollover ?? 0, borrowed: b?.borrowed ?? 0,
    weekStories: Math.floor(weekly / SEED_COSTS.build_story), low: pct >= 90, backWhen, into, runsOut,
  }
}

/** A short "−12" whenever the total drops (no flying seeds: Brian, Oct 22). */
function useTick(total: number, ready: boolean) {
  const last = useRef<number | null>(null)
  const [tick, setTick] = useState<{ n: number; id: number } | null>(null)
  useEffect(() => {
    if (!ready) return
    if (last.current != null && total !== last.current) {
      const n = total - last.current
      setTick({ n, id: Date.now() })
      const t = window.setTimeout(() => setTick(null), 1300)
      last.current = total
      return () => window.clearTimeout(t)
    }
    last.current = total
  }, [total, ready])
  return tick
}

function Tick({ tick }: { tick: { n: number; id: number } | null }) {
  if (!tick) return null
  return <span key={tick.id} className={`gg-tick ${tick.n < 0 ? 'gg-tick--down' : 'gg-tick--up'}`}>{tick.n < 0 ? '−' : '+'}{Math.abs(tick.n)}</span>
}

/**
 * The phone's seed chip (topbar.js v6): the seeds as a number, the week's meter
 * beside it, and a plain amber + when purchased seeds stand behind this week's.
 * Tapping it opens the seed panel.
 */
export function SeedChip({ open, onToggle }: { open: boolean; onToggle: () => void }) {
  const v = useSeedView()
  const tick = useTick(v.total, !!v.b)
  return (
    <button
      type="button"
      className={`gg-seedchip${v.low ? ' is-low' : ''}${tick && tick.n < 0 ? ' is-spent' : ''}`}
      data-seed-bank
      onClick={onToggle}
      aria-expanded={open}
      aria-label={`${v.total} seeds: ${v.weekly} this week${v.purchased ? `, ${v.purchased} purchased` : ''}. Tap for details.`}
    >
      <SeedGlyph className="gg-seedchip__glyph" />
      <span className="gg-seedchip__num">{v.total.toLocaleString('en-US')}<small>seeds</small></span>
      <span className="gg-seedchip__meter" aria-hidden="true"><i style={{ width: `${100 - v.pct}%` }} /></span>
      <span className="gg-seedchip__plus" aria-hidden="true" hidden={!(v.purchased > 0)}>+</span>
      <Tick tick={tick} />
    </button>
  )
}

/**
 * The desktop sidebar's meter (desktop-nav.js v3.2, Brian's desktop log #007:
 * "see usage not in stories but in percentage left, show if they are on track
 * to run out"): this week's share left, its bar, and the pace. It opens the
 * seed panel beside the sidebar.
 */
export function SeedMeter({ open, onToggle }: { open: boolean; onToggle: () => void }) {
  const v = useSeedView()
  const tick = useTick(v.total, !!v.b)
  return (
    <button type="button" className={`gg-deskmeter${v.low ? ' is-low' : ''}${tick && tick.n < 0 ? ' is-spent' : ''}`} data-seed-bank
      onClick={onToggle} aria-expanded={open} title="Your seeds: what's left, your pace, what's using them">
      <span className="gg-deskmeter__r"><span>This week</span><b>{100 - v.pct}%<small> left</small></b></span>
      <span className="gg-deskmeter__bar"><i style={{ width: `${100 - v.pct}%` }} /></span>
      <span className="gg-deskmeter__c">{v.runsOut ? `At this pace, out ${v.runsOut}` : 'On track to last until Monday'}</span>
      <Tick tick={tick} />
    </button>
  )
}

/**
 * This week's spends by what they paid for, biggest first (Brian's GG.usage(),
 * app-data.js v5.9): the phone's seed panel and the computer's read the same
 * list. From the ledger since the week began; a borrow is not a spend.
 */
const USAGE: [string, RegExp][] = [
  ['Stories', /^build_story$/],
  ['Pull 5 more', /^reroll$/],
  ['Rewrites and positions', /^(regenerate|voice_build|reformat)$/],
  ['Newsletter versions', /^newsletter_version$/],
  ['Moved to your alert budget', /^alert_budget$/],
  ['Places and topic resets', /^(add_region|reset_mix|swap_topic)$/],
  ['Keyword checks', /^pill_research$/],
  ['Emma questions', /^advisor_(question|topup)$/],
]
export function useSeedUsage(weekStart: string | undefined): [string, number][] {
  const clientId = useEffectiveClientId()
  // Under 'seed-history', so every spend refreshes it.
  const { data } = useQuery({
    queryKey: ['seed-history', clientId, 'week', weekStart],
    enabled: !!clientId && !!weekStart,
    queryFn: async () => {
      const { data, error } = await supabase.from('seed_ledger').select('action, delta')
        .eq('client_id', clientId!).lt('delta', 0)
        .gte('created_at', new Date(weekStart + 'T00:00:00').toISOString())
      if (error) throw error
      return data as Pick<SeedLedgerEntry, 'action' | 'delta'>[]
    },
  })
  const by: Record<string, number> = {}
  for (const r of data ?? []) {
    if (r.action === 'borrow') continue
    const c = USAGE.find(([, rx]) => rx.test(r.action))?.[0] ?? 'Other'
    by[c] = (by[c] ?? 0) + -r.delta
  }
  return Object.entries(by).sort((a, b) => b[1] - a[1])
}

/** Add seeds' three packs (desktop-nav.js v3.2, topbar.js Oct 29). */
const PACKS = [120, 360, 1200]
const FINE = 'Added seeds don’t expire and are used after this week’s.'
// Billing isn't wired (Brian: "prices come with billing", the handoff: "billing is yours"); the
// prototype adds them free, the app says so plainly instead of pretending.
const NOT_YET = 'Buying seeds isn’t switched on yet. If you run short, get in touch and we’ll top you up.'

function when(iso: string): string {
  const d = new Date(iso), today = new Date()
  return d.toDateString() === today.toDateString()
    ? d.toLocaleTimeString('en-US', { hour: 'numeric', minute: '2-digit' })
    : d.toLocaleDateString('en-US', { weekday: 'short' })
}

/** One spend can come out of two pots; the Recent list shows it once. */
function recent(rows: SeedLedgerEntry[] | undefined): { id: string; label: string; at: string; n: number }[] {
  const out: { id: string; label: string; at: string; n: number; key: string }[] = []
  for (const r of rows ?? []) {
    if (r.delta >= 0) continue
    const key = `${r.action}|${r.ref_id ?? ''}|${r.created_at.slice(0, 19)}`
    const prev = out.find((o) => o.key === key)
    if (prev) { prev.n += -r.delta; continue }
    out.push({ id: r.id, key, label: LEDGER_LABELS[r.action] ?? r.action.replace(/_/g, ' '), at: r.created_at, n: -r.delta })
  }
  return out.slice(0, 6)
}

/**
 * The seed panel, the same on the phone and the computer (Brian's desktop log
 * #007, Oct 29; topbar.js "to match the computer's seed panel"): the share of
 * this week left, whether the pace lasts to Monday, what this week's seeds went
 * to, the alert and public-records budgets, and Add seeds. On a computer it
 * rises beside the sidebar with a close; on the phone it drops from the chip
 * and keeps the purchased and bonus seeds, what uses seeds, Recent and the plan.
 */
export function SeedPanel({ onClose, placement }: { onClose: () => void; placement: 'phone' | 'desk' }) {
  const v = useSeedView()
  const { data: plan } = usePlan()
  const { data: history } = useSeedHistory(24)
  const use = useSeedUsage(v.b?.week_start)
  const { data: w } = useWatchState()
  const { data: r } = useRecordsState()
  const [fine, setFine] = useState<string | null>(null)
  const ref = useRef<HTMLDivElement>(null)
  const desk = placement === 'desk'

  useEffect(() => {
    const close = (e: MouseEvent) => {
      const t = e.target as HTMLElement
      if (ref.current?.contains(t) || t.closest('[data-seed-bank]')) return
      onClose()
    }
    const esc = (e: KeyboardEvent) => { if (e.key === 'Escape') onClose() }
    document.addEventListener('mousedown', close)
    document.addEventListener('keydown', esc)
    return () => { document.removeEventListener('mousedown', close); document.removeEventListener('keydown', esc) }
  }, [onClose])

  if (!v.b) return null
  const build = SEED_COSTS.build_story
  const rows = recent(history)
  const left = 100 - v.pct
  const tot = use.reduce((n, [, x]) => n + x, 0) || 1
  const pace = v.runsOut
    ? <div className="gg-sp__pace is-warn">At this pace this week’s seeds run out <b>{v.runsOut}</b>{v.purchased > 0 && (desk
        ? <>. After that, stories come from your <b>{v.purchased.toLocaleString('en-US')}</b> extra seeds</>
        : <>. Then your <b>{v.purchased.toLocaleString('en-US')}</b> extra seeds take over</>)}. They come back Monday.</div>
    : desk
      ? <div className="gg-sp__pace">On track: at this pace this week’s seeds <b>last until Monday</b>, when they refill.</div>
      : <div className="gg-sp__pace">On track: this week’s seeds <b>last until Monday</b>.</div>
  return (
    <div ref={ref} className={`gg-seedpanel gg-seedpanel--${placement}${v.low ? ' is-low' : ''}`} role="dialog" aria-label="Your seeds"
      // a tap on the phone's panel closes it; its buttons and links don't (topbar.js, Oct 29)
      onClick={desk ? undefined : (e) => { if (!(e.target as HTMLElement).closest('a, button')) onClose() }}>
      {desk && (
        <div className="gg-sp__hd"><h3>Your seeds</h3>
          <button type="button" className="gg-sp__x" onClick={onClose} aria-label="Close">
            <svg viewBox="0 0 24 24" aria-hidden="true"><path d="M6 6l12 12M18 6L6 18" /></svg>
          </button>
        </div>
      )}
      <div className="gg-sp__big"><b>{left}%</b>
        {desk
          ? <span>of this week’s {v.week.toLocaleString('en-US')} left{v.purchased > 0 ? ` · plus ${v.purchased.toLocaleString('en-US')} extra` : ''}</span>
          : <span>of this week’s {v.week.toLocaleString('en-US')} left<br />≈ {v.weekStories} {v.weekStories === 1 ? 'story' : 'stories'} · back {v.backWhen}{v.rollover > 0 ? ` · ${v.rollover} rolled over` : ''}</span>}
      </div>
      <div className="gg-seedpanel__bar gg-sp__bar"><i style={{ width: `${left}%` }} /></div>
      {pace}
      <div className="gg-sp__k">What’s using them this week</div>
      {use.length ? (
        <div className="gg-sp__use">
          {use.map(([c, x]) => {
            const pc = Math.round((100 * x) / tot)
            return <Fragment key={c}><span>{c}</span><em>{pc}%</em><span className="gg-sp__ln"><i style={{ width: `${pc}%` }} /></span></Fragment>
          })}
        </div>
      ) : <div className="gg-sp__none">Nothing yet this week.</div>}
      {(w || r) && (
        <>
          <div className="gg-sp__k">Their own budgets</div>
          <div className="gg-sp__bud">
            {w && <Link to={ROUTES.breaking} onClick={onClose}><small>Alert budget</small><b>{w.bank} of {w.cap}</b></Link>}
            {r && <Link to={ROUTES.recordsSettings} onClick={onClose}><small>{desk ? 'Public records this week' : 'Records this week'}</small><b>{r.spent} of {r.cap}</b></Link>}
          </div>
        </>
      )}
      <div className="gg-sp__k">Add seeds</div>
      <div className="gg-sp__packs">
        {PACKS.map((n) => (
          <button key={n} type="button" data-buy={n} onClick={() => setFine(NOT_YET)}>
            +{n.toLocaleString('en-US')}<small>{desk ? 'about' : '≈'} {Math.floor(n / build)} stories</small>
          </button>
        ))}
      </div>
      <p className="gg-sp__fine" aria-live="polite">{fine ? <b>{fine}</b> : FINE}</p>
      {!desk && (
        <>
          <div className="gg-seedpanel__row"><span>Purchased</span><b>{v.purchased}</b></div>
          <div className="gg-seedpanel__cap">≈ {Math.floor(v.purchased / build)} more stories · never expire · used after this week’s</div>
          {v.granted > 0 && (
            <>
              <div className="gg-seedpanel__row"><span>Bonus</span><b>{v.granted}</b></div>
              <div className="gg-seedpanel__cap">from coins</div>
            </>
          )}
          <div className="gg-seedpanel__list">
            <span className="gg-seedpanel__k">What uses seeds</span>
            {PRICE_LIST.map((p) => (
              <div key={p.label}><span>{p.label}{p.note && <small> ({p.note})</small>}</span><b>{p.seeds}</b></div>
            ))}
          </div>
          {rows.length > 0 && (
            <div className="gg-seedpanel__list">
              <span className="gg-seedpanel__k">Recent</span>
              {rows.map((x) => (
                <div key={x.id}><span>{x.label} · {when(x.at)}</span><b>−{x.n}</b></div>
              ))}
            </div>
          )}
          <div className="gg-seedpanel__plan"><span>Plan</span><b>{plan?.name ?? ''}</b></div>
        </>
      )}
    </div>
  )
}
